import api from "@/lib/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  Loader2,
  Save,
  UploadCloud,
} from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import InputError from "@/components/InputError";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useDataStep from "@/hooks/useDataStep";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { DataKepemimpinan as DataKepemimpinanType } from "@/types";

// Definisikan Enum List
export const listLingkup = [
  { label: "Sekolah", value: "sekolah" },
  { label: "Luar Sekolah", value: "luar_sekolah" },
];

export const listJabatan = [
  { label: "Ketua", value: "ketua" },
  { label: "Wakil Ketua", value: "wakil_ketua" },
  { label: "Sekretaris", value: "sekretaris" },
  { label: "Bendahara", value: "bendahara" },
];

const saveDataKepemimpinan = async (form: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/kepemimpinan/store",
    form,
    { withCredentials: true },
  );
  return response.data;
};

const getDataKepemimpinan = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/kepemimpinan/index?id_sys_periode=${id_sys_periode}`,
  );
  return response.data?.data;
};

export default function DataKepemimpinan({
  idDataPendaftaran,
}: {
  idDataPendaftaran?: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [form, setForm] = useState({
    id_data_pendaftaran: idDataPendaftaran || "",
    nama_organisasi: "",
    lingkup: "",
    jabatan: "",
    nomor_sk: "",
    tahun_sk: "",
  });

  const [fileSk, setFileSk] = useState<File | null>(null);

  // 1. Fetch Data
  const { data, isPending: isFetchingData } = useQuery({
    queryKey: ["dataKepemimpinan", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataKepemimpinan(sessionPeriode?.id_sys_periode || ""),
    enabled: !!sessionPeriode?.id_sys_periode,
  });

  // Ekstrak data (menangani jika response berupa array atau object tunggal)
  const existingData: DataKepemimpinanType | null =
    data && Array.isArray(data) ? data[0] : data;
  const hasExistingFile = !!existingData?.file_sk;

  // 2. Set Default Value ke Form
  useEffect(() => {
    if (existingData) {
      setForm({
        id_data_pendaftaran:
          existingData.id_data_pendaftaran || idDataPendaftaran || "",
        nama_organisasi: existingData.nama_organisasi || "",
        lingkup: existingData.lingkup || "",
        jabatan: existingData.jabatan || "",
        nomor_sk: existingData.nomor_sk || "",
        tahun_sk: existingData.tahun_sk || "",
      });
    }
  }, [existingData, idDataPendaftaran]);

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: saveDataKepemimpinan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep"],
      });
      queryClient.invalidateQueries({
        queryKey: ["dataKepemimpinan"],
      });
      toast.success(data?.message || "Data kepemimpinan berhasil disimpan.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "Terjadi kesalahan yang tidak terduga.";
      toast.error(errorMessage);
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "tahun_sk") {
      if (value.length <= 4) {
        setForm((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setForm({ ...form, [name]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

    if (file) {
      // 1. Validasi Ekstensi/Tipe File (Hanya PDF)
      if (file.type !== "application/pdf") {
        toast.error("Format file tidak valid. Harap unggah file PDF.");
        e.target.value = ""; // Reset input
        return;
      }

      // 2. Validasi Ukuran File (Maksimal 2MB)
      if (file.size > MAX_FILE_SIZE) {
        toast.error("Ukuran file SK terlalu besar. Maksimal 2MB.");
        e.target.value = ""; // Reset input
        return;
      }

      setFileSk(file);
    } else {
      setFileSk(null);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);
    formData.append("nama_organisasi", form.nama_organisasi);
    formData.append("lingkup", form.lingkup);
    formData.append("jabatan", form.jabatan);
    formData.append("nomor_sk", form.nomor_sk);
    formData.append("tahun_sk", form.tahun_sk);

    if (fileSk) {
      formData.append("file_sk", fileSk);
    } else if (!hasExistingFile) {
      // Jika tidak ada file baru dan sebelumnya juga belum ada file
      toast.error("Silakan unggah File SK terlebih dahulu.");
      return;
    }

    mutate(formData);
  };

  const { isSudahFinalisasi, isValidasiPendaftaran } = useDataStep();
  const { timePassedActive } = useTimePeriode();

  const isDisableInput =
    isSudahFinalisasi ||
    isValidasiPendaftaran ||
    timePassedActive?.waktu_selesai_daftar_valid_selesai;

  const apiErrors = error?.response?.data?.errors;
  const isLoading = isPending || isFetchingData;

  return (
    <Card className="w-full shadow-sm border-slate-200 dark:border-slate-800 animate-in fade-in duration-500">
      <CardHeader className="border-b border-slate-100 dark:border-slate-800/60 pb-5 md:pb-6">
        <CardTitle className="text-xl mb-4">Pengalaman Kepemimpinan</CardTitle>

        {/* Alert Warning Box */}
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-4 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
          <div className="flex flex-col text-sm text-amber-800 dark:text-amber-200/90 leading-relaxed">
            <p>
              <strong>Jalur Kepemimpinan</strong> mewajibkan pendaftar memiliki
              SK sebagai{" "}
              <strong>Ketua / Wakil Ketua / Sekretaris / Bendahara</strong>{" "}
              organisasi saat SMA/SMK sederajat.
            </p>
            <p className="mt-1 font-medium text-amber-900 dark:text-amber-100">
              * Maksimal 1 SK yang dapat diunggah.
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nama Organisasi */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="nama_organisasi">
                Nama Organisasi <span className="text-destructive">*</span>
              </Label>
              <Input
                id="nama_organisasi"
                name="nama_organisasi"
                placeholder="Contoh: OSIS SMA Negeri 1 Singaraja"
                value={form.nama_organisasi}
                onChange={handleChange}
                disabled={isLoading}
              />
              <InputError text={apiErrors?.nama_organisasi?.join(", ")} />
            </div>

            {/* Lingkup */}
            <div className="space-y-2">
              <Label htmlFor="lingkup">
                Lingkup Tingkat <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.lingkup}
                onValueChange={(value) => {
                  if (!value) return;
                  handleSelectChange("lingkup", value);
                }}
                disabled={isLoading}
              >
                <SelectTrigger id="lingkup">
                  <SelectValue placeholder="Pilih Lingkup" />
                </SelectTrigger>
                <SelectContent>
                  {listLingkup.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <InputError text={apiErrors?.lingkup?.join(", ")} />
            </div>

            {/* Jabatan */}
            <div className="space-y-2">
              <Label htmlFor="jabatan">
                Jabatan <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.jabatan}
                onValueChange={(value) => {
                  if (!value) return;
                  handleSelectChange("jabatan", value);
                }}
                disabled={isLoading}
              >
                <SelectTrigger id="jabatan">
                  <SelectValue placeholder="Pilih Jabatan" />
                </SelectTrigger>
                <SelectContent>
                  {listJabatan.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <InputError text={apiErrors?.jabatan?.join(", ")} />
            </div>

            {/* Nomor SK */}
            <div className="space-y-2">
              <Label htmlFor="nomor_sk">
                Nomor SK <span className="text-destructive">*</span>
              </Label>
              <Input
                id="nomor_sk"
                name="nomor_sk"
                placeholder="Masukkan Nomor SK pengangkatan"
                value={form.nomor_sk}
                onChange={handleChange}
                disabled={isLoading}
              />
              <InputError text={apiErrors?.nomor_sk?.join(", ")} />
            </div>

            {/* Tahun SK */}
            <div className="space-y-2">
              <Label htmlFor="tahun_sk">
                Tahun SK <span className="text-destructive">*</span>
              </Label>
              <Input
                id="tahun_sk"
                name="tahun_sk"
                type="number"
                min="1000"
                max="9999"
                placeholder="Contoh: 2023"
                value={form.tahun_sk}
                onChange={handleChange}
                disabled={isLoading}
                onWheel={(e) => e.currentTarget.blur()}
              />
              <InputError text={apiErrors?.tahun_sk?.join(", ")} />
            </div>

            {/* File Upload SK */}
            <div className="space-y-2 md:col-span-2 border border-dashed rounded-xl p-5 border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20">
              <Label htmlFor="file_sk" className="flex items-center gap-2 mb-1">
                <UploadCloud className="w-4 h-4 text-slate-500" />
                Unggah File SK{" "}
                {!hasExistingFile && (
                  <span className="text-destructive">*</span>
                )}
              </Label>
              <p className="text-xs text-muted-foreground mb-4">
                Format yang diizinkan: PDF Ukuran maksimal 1MB.
                {hasExistingFile && " (Abaikan jika tidak ingin mengubah file)"}
              </p>

              {/* Tampilkan indikator jika file sudah ada di database */}
              {hasExistingFile && (
                <div className="flex flex-wrap items-center gap-3 p-3 mb-4 rounded-lg bg-emerald-50 border border-emerald-100 dark:bg-emerald-950/30 dark:border-emerald-900/50">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-400">
                      SK Sudah Terunggah
                    </span>
                    <span className="text-xs text-emerald-600/80 dark:text-emerald-500/80">
                      File sebelumnya telah tersimpan di sistem.
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900"
                    onClick={() => window.open(existingData?.file_sk, "_blank")}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> Lihat File
                  </Button>
                </div>
              )}

              <Input
                id="file_sk"
                name="file_sk"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                disabled={isLoading || isDisableInput}
                className="cursor-pointer file:cursor-pointer file:text-primary file:font-semibold bg-white dark:bg-slate-950"
              />

              {/* Keterangan tambahan di bagian bawah */}
              <p className="text-sm text-muted-foreground mt-2">
                Unggah SK kepemimpinan yang mencantumkan nama Anda sebagai
                Ketua/Wakil/Sekretaris/Bendahara, dilengkapi tanda tangan dan
                cap resmi.
              </p>

              <InputError text={apiErrors?.file_sk?.join(", ")} />
            </div>
          </div>

          {/* Action Buttons */}
          {!isDisableInput ? (
            <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800/60">
              <Button
                type="submit"
                variant="default"
                className="w-full sm:w-auto min-w-[140px]"
                disabled={isLoading || (!fileSk && !hasExistingFile)}
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Menyimpan...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {hasExistingFile ? "Simpan Perubahan" : "Simpan Data"}
                  </>
                )}
              </Button>
            </div>
          ) : (
            ""
          )}
        </form>
      </CardContent>
    </Card>
  );
}
